import React, { useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  X, 
  Star, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  Building2, 
  Target,
  Zap,
  Shield,
  TrendingUp,
  Share2,
  ExternalLink
} from 'lucide-react';

const TestimonialModal = ({ testimonial, isOpen, onClose }) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${testimonial.clientCompany} - Envael Success Story`,
        text: testimonial.testimonial,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !testimonial) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end justify-center"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-background rounded-t-2xl shadow-2xl transform transition-transform duration-300 ease-out">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-6 rounded-t-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={testimonial.clientAvatar}
                  alt={testimonial.clientName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-primary/20"
                />
                {testimonial.verified && (
                  <CheckCircle className="absolute -bottom-1 -right-1 h-5 w-5 text-green-500 bg-white rounded-full" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {testimonial.clientName}
                </h2>
                <p className="text-lg text-primary font-medium">
                  {testimonial.clientTitle}
                </p>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span>{testimonial.clientCompany}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {renderStars(testimonial.rating)}
              <span className="text-sm text-muted-foreground ml-2">
                ({testimonial.rating}/5)
              </span>
            </div>
          </div>

          {/* Project Info */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Badge variant="secondary" className="text-sm px-3 py-1">
              {testimonial.projectType}
            </Badge>
            <Badge variant="outline" className="text-sm px-3 py-1">
              {testimonial.clientIndustry}
            </Badge>
            {testimonial.featured && (
              <Badge className="text-sm px-3 py-1 bg-primary text-primary-foreground">
                Featured Success Story
              </Badge>
            )}
          </div>

          {/* Location and Date */}
          <div className="flex items-center gap-6 text-muted-foreground mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{testimonial.clientLocation}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{new Date(testimonial.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={handleShare}>
              <Share2 className="mr-2 h-4 w-4" />
              Share Story
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="mr-2 h-4 w-4" />
              Close
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
          {/* Main Testimonial */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <blockquote className="text-lg text-muted-foreground leading-relaxed mb-6">
                "{testimonial.fullTestimonial}"
              </blockquote>
              
              {/* Project Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-border">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Project Duration</p>
                    <p className="font-semibold">{testimonial.projectDuration}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Project Value</p>
                    <p className="font-semibold">{testimonial.projectValue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Target className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-semibold">{testimonial.category}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Key Results & Impact
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonial.results.map((result, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{result}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Challenges & Solutions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-orange-500" />
                  Challenges Faced
                </h3>
                <div className="space-y-3">
                  {testimonial.challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2"></div>
                      <span className="text-muted-foreground">{challenge}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Our Solutions
                </h3>
                <div className="space-y-3">
                  {testimonial.solutions.map((solution, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                      <span className="text-muted-foreground">{solution}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tags */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-foreground mb-4">Technologies & Services</h3>
              <div className="flex flex-wrap gap-2">
                {testimonial.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                    {tag}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <Card className="bg-primary text-primary-foreground">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold mb-4">
                Ready to Create Your Success Story?
              </h3>
              <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
                Join {testimonial.clientCompany} and other successful businesses that have transformed their operations with Envael's innovative solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="secondary" size="lg" asChild>
                  <a href="/contact">
                    Start Your Project
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button variant="outline" size="lg" className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" asChild>
                  <a href="/services">
                    View Our Services
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TestimonialModal;
